import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Send, Users, BarChart3, Headphones, CheckCircle2 } from "lucide-react";
import { SPONSOR_TIERS } from "@/data/site";
import StudioBackdrop from "@/components/podcast/StudioBackdrop";

const TYPES = [
  { value: "episode", label: "Episode Drop" },
  { value: "season", label: "Season Partner" },
  { value: "segment", label: "Segment Integration" },
  { value: "custom", label: "Brand Takeover" },
];

const INITIAL = {
  company_name: "",
  contact_name: "",
  email: "",
  website: "",
  budget_range: "",
  sponsorship_type: "episode",
  message: "",
};

export default function Sponsorship() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/sponsorship-inquiries", form);
      toast.success("Inquiry received. We'll be in touch within 48hrs.");
      setSubmitted(true);
      setForm(INITIAL);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Submission failed";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="sponsorship-page" className="pt-32 pb-20 min-h-screen bg-[#0A0A0A] relative">
      <StudioBackdrop opacity={0.1} size={260} />
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-96 h-96 halftone opacity-20 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.25em] uppercase font-bold mb-3">
              <span className="text-[#1E6FE0]">His.</span>{" "}
              <span className="text-[#E10600]">Hers.</span>{" "}
              <span className="text-zinc-500">Your Brand.</span>
            </p>
            <h1 className="font-display text-6xl md:text-8xl uppercase tracking-tight leading-[0.9]">
              Partner With<br /><span className="text-[#E10600]">The</span> <span className="text-[#1E6FE0]">Show</span>
            </h1>
            <p className="mt-6 text-zinc-300 text-lg">
              Reach a deeply engaged audience evenly split across men and women — both
              actively listening on relationships, dating, marriage, and modern love.
              Our listeners don't skip ads — they trust the host.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Tiers */}
        <section className="mb-20">
          <h2 className="font-display text-5xl md:text-6xl uppercase leading-none mb-3">
            Sponsorship <span className="text-[#E10600]">Tiers</span>
          </h2>
          <p className="text-zinc-400 mb-10 max-w-2xl">
            Custom packages available. All deals include host approval and integration
            in the brand's voice — no awkward ad reads.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
            {SPONSOR_TIERS.map((tier, i) => {
              const tierColor = tier.accent === "blue" ? "#1E6FE0" : "#E10600";
              return (
                <div
                  key={tier.name}
                  data-testid={`sponsor-tier-${i}`}
                  className={`p-8 flex flex-col ${
                    tier.featured ? "bg-[#121212] relative" : "bg-[#0A0A0A]"
                  }`}
                >
                  {tier.featured && (
                    <span
                      className="absolute -top-3 left-8 px-3 py-1 text-white text-[10px] font-bold tracking-widest"
                      style={{ background: tierColor }}
                    >
                      MOST POPULAR
                    </span>
                  )}
                  <p
                    className="text-xs tracking-[0.2em] uppercase font-mono"
                    style={{ color: tierColor }}
                  >
                    Tier {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="font-display text-4xl uppercase leading-none mt-2">
                    {tier.name}
                  </h3>
                  <p className="mt-3 font-mono text-zinc-300">{tier.price}</p>

                  <ul className="mt-6 space-y-3 flex-1">
                    {tier.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm text-zinc-300">
                        <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: tierColor }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href="#sponsor-form"
                    data-testid={`sponsor-tier-cta-${i}`}
                    className={`mt-8 ${tier.featured ? "btn-primary" : tier.accent === "blue" ? "btn-blue" : "btn-secondary"} justify-center`}
                  >
                    Get Started
                  </a>
                </div>
              );
            })}
          </div>
        </section>

        {/* Form */}
        <section id="sponsor-form" className="grid grid-cols-1 lg:grid-cols-12 gap-12 border-t border-[#262626] pt-16">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E10600] mb-3">
              Apply
            </p>
            <h2 className="font-display text-5xl md:text-6xl uppercase leading-none">
              Sponsorship<br /><span className="text-[#E10600]">Inquiry</span>
            </h2>
            <p className="mt-6 text-zinc-400">
              Tell us about your brand and goals. We'll send back a custom deck with
              audience data, integration examples, and pricing within 48 hours.
            </p>
          </div>
          <div className="lg:col-span-7">
            {submitted ? (
              <div data-testid="sponsor-success" className="border border-[#E10600] p-10 bg-[#121212] text-center">
                <CheckCircle2 className="w-12 h-12 text-[#E10600] mx-auto mb-4" />
                <h3 className="font-display text-4xl uppercase">Inquiry Received</h3>
                <p className="mt-4 text-zinc-400">
                  Our partnerships team will follow up within 48 hours with next steps.
                </p>
                <button
                  data-testid="sponsor-send-another"
                  onClick={() => setSubmitted(false)}
                  className="btn-secondary mt-8"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form
                data-testid="sponsor-form"
                onSubmit={submit}
                className="border border-[#262626] bg-[#0F0F0F] p-6 md:p-10 space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Company *">
                    <input data-testid="sponsor-input-company" required value={form.company_name} onChange={update("company_name")} className="input-edge" placeholder="Your Brand" />
                  </Field>
                  <Field label="Contact Name *">
                    <input data-testid="sponsor-input-contact" required value={form.contact_name} onChange={update("contact_name")} className="input-edge" placeholder="Your name" />
                  </Field>
                  <Field label="Email *">
                    <input data-testid="sponsor-input-email" required type="email" value={form.email} onChange={update("email")} className="input-edge" placeholder="you@brand.com" />
                  </Field>
                  <Field label="Website">
                    <input data-testid="sponsor-input-website" value={form.website} onChange={update("website")} className="input-edge" placeholder="https://yourbrand.com" />
                  </Field>
                </div>

                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 font-mono block mb-3">
                    Sponsorship Type
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {TYPES.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        data-testid={`sponsor-type-${t.value}`}
                        onClick={() => setForm({ ...form, sponsorship_type: t.value })}
                        className={`px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold border transition-colors ${
                          form.sponsorship_type === t.value
                            ? "border-[#E10600] bg-[#E10600] text-white"
                            : "border-[#262626] text-zinc-400 hover:text-white"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <Field label="Budget Range">
                  <input data-testid="sponsor-input-budget" value={form.budget_range} onChange={update("budget_range")} className="input-edge" placeholder="$5K – $50K" />
                </Field>

                <Field label="Tell us about your goals *">
                  <textarea data-testid="sponsor-input-message" required rows={5} value={form.message} onChange={update("message")} className="input-edge resize-none" placeholder="What are you trying to accomplish?" />
                </Field>

                <div className="pt-4 flex flex-wrap items-center gap-4 justify-between">
                  <p className="text-xs text-zinc-500 font-mono">All inquiries reviewed personally.</p>
                  <button
                    data-testid="sponsor-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="btn-primary disabled:opacity-50"
                  >
                    {loading ? "Sending…" : "Submit Inquiry"}
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 font-mono block mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
